/**
 * Logger - Centralized logging with configurable levels
 * 
 * IMPORTANT: Never use console.* directly in game code (except in Logger implementation,
 * crash fallback, or dev utilities). Always use Logger methods with optional chaining.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

interface LogLevels {
  readonly debug: 0;
  readonly info: 1;
  readonly warn: 2;
  readonly error: 3;
}

export interface LoggerConfig {
  isDebugEnabled: () => boolean;
  getLogLevel: () => LogLevel;
}

export interface LoggerInterface {
  debug(message: string, context?: string): void;
  info(message: string, context?: string): void;
  warn(message: string, context?: string): void;
  error(message: string, error?: Error, context?: string): void;
  group<T>(title: string, callback: () => T): T;
  time<T>(label: string, callback: () => T): T;
  createChildLogger?: (context: string) => LoggerInterface;
}

export class Logger implements LoggerInterface {
  private readonly config: LoggerConfig | null;
  private readonly levels: LogLevels = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  } as const;

  // Cached filter configuration - computed once on first use
  private filtersCached = false;
  private cachedIncludes: string[] = [];
  private cachedExcludes: string[] = [];

  constructor(config: LoggerConfig | null = null) {
    this.config = config;
  }

  /**
   * Cache filter configuration on first use.
   * Filters only change on page reload, so no need to re-read every call.
   */
  private ensureFiltersCached(): void {
    if (this.filtersCached) return;
    this.filtersCached = true;

    try {
      const params = (() => {
        try {
          return new URLSearchParams(globalThis?.location?.search || "");
        } catch {
          return new URLSearchParams("");
        }
      })();

      const urlInclude = params.get("logInclude") || params.get("log") || "";
      const urlExclude = params.get("logExclude") || "";
      const lsInclude = (() => {
        try {
          return globalThis?.localStorage?.getItem("game.log.include") || "";
        } catch {
          return "";
        }
      })();
      const lsExclude = (() => {
        try {
          return globalThis?.localStorage?.getItem("game.log.exclude") || "";
        } catch {
          return "";
        }
      })();

      const includeRaw = (urlInclude || lsInclude || "").trim();
      const excludeRaw = (urlExclude || lsExclude || "").trim();

      this.cachedIncludes = includeRaw
        ? includeRaw
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean)
        : [];
      this.cachedExcludes = excludeRaw
        ? excludeRaw
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean)
        : [];
    } catch {
      // If filters cannot be evaluated, use empty arrays
      this.cachedIncludes = [];
      this.cachedExcludes = [];
    }
  }

  /**
   * Check if logging is enabled for the given level
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.config?.isDebugEnabled()) return false;

    const configLogLevel = this.config.getLogLevel();
    const currentLevel = this.levels[configLogLevel] ?? 1;
    const messageLevel = this.levels[level] ?? 1;

    return messageLevel >= currentLevel;
  }

  /**
   * Format log message with timestamp and context
   */
  private formatMessage(
    level: LogLevel,
    message: string,
    context?: string,
  ): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    if (context) {
      return `${prefix} [${context}] ${message}`;
    }
    return `${prefix} ${message}`;
  }

  /**
   * Determine if this log line should pass include/exclude filters.
   * Uses cached filter configuration for performance.
   */
  private passesFilters(message: string, context?: string): boolean {
    this.ensureFiltersCached();

    // If includes are specified, require at least one to match
    if (this.cachedIncludes.length > 0) {
      const hay = `${context || ""} ${message}`.toLowerCase();
      const match = this.cachedIncludes.some((tok) => hay.includes(tok));
      if (!match) return false;
    }
    // If any exclude matches, drop the line
    if (this.cachedExcludes.length > 0) {
      const hay = `${context || ""} ${message}`.toLowerCase();
      const match = this.cachedExcludes.some((tok) => hay.includes(tok));
      if (match) return false;
    }
    return true;
  }

  /**
   * Debug level logging
   */
  debug(message: string, context?: string): void {
    if (this.shouldLog("debug") && this.passesFilters(message, context)) {
      console.debug(this.formatMessage("debug", message, context));
    }
  }

  /**
   * Info level logging
   */
  info(message: string, context?: string): void {
    if (this.shouldLog("info") && this.passesFilters(message, context)) {
      console.info(this.formatMessage("info", message, context));
    }
  }

  /**
   * Warning level logging
   */
  warn(message: string, context?: string): void {
    if (this.shouldLog("warn") && this.passesFilters(message, context)) {
      console.warn(this.formatMessage("warn", message, context));
    }
  }

  /**
   * Error level logging
   */
  error(message: string, error?: Error, context?: string): void {
    if (this.shouldLog("error") && this.passesFilters(message, context)) {
      let errorMessage = message;
      if (error) {
        errorMessage += `: ${error.message}`;
        if (error.stack && this.config?.isDebugEnabled()) {
          errorMessage += `\nStack trace: ${error.stack}`;
        }
      }
      console.error(this.formatMessage("error", errorMessage, context));
    }
  }

  /**
   * Group logging operations
   */
  group<T>(title: string, callback: () => T): T {
    if (this.shouldLog("debug")) {
      console.group(this.formatMessage("debug", `Group: ${title}`));
      try {
        return callback();
      } finally {
        console.groupEnd();
      }
    } else {
      return callback();
    }
  }

  /**
   * Time a logging operation
   */
  time<T>(label: string, callback: () => T): T {
    if (this.shouldLog("debug")) {
      const startTime = performance.now();
      try {
        return callback();
      } finally {
        const endTime = performance.now();
        const duration = endTime - startTime;
        this.debug(`${label} completed in ${duration.toFixed(2)}ms`);
      }
    } else {
      return callback();
    }
  }

  /**
   * Create a child logger with a specific context
   */
  createChildLogger(context: string): LoggerInterface {
    return {
      debug: (message: string, childContext?: string) =>
        this.debug(
          message,
          childContext ? `${context}:${childContext}` : context,
        ),
      info: (message: string, childContext?: string) =>
        this.info(
          message,
          childContext ? `${context}:${childContext}` : context,
        ),
      warn: (message: string, childContext?: string) =>
        this.warn(
          message,
          childContext ? `${context}:${childContext}` : context,
        ),
      error: (message: string, error?: Error, childContext?: string) =>
        this.error(
          message,
          error,
          childContext ? `${context}:${childContext}` : context,
        ),
      group: <T>(title: string, callback: () => T) =>
        this.group(`${context}: ${title}`, callback),
      time: <T>(label: string, callback: () => T) =>
        this.time(`${context}: ${label}`, callback),
    };
  }
}
